/**
 * hatchConverter — HATCH → CadPrimitive[] (PDF-5a).
 *
 * Three fidelity tiers, matching `PLAN-plot-pdf.md` §5 (PDF-5 row) + §7.8:
 *
 *   P1 — SOLID fill (`solidFill === true`). Each boundary path becomes a closed
 *        subpath (line → line; arc → exact cubic bezier; ellipticArc →
 *        axis-rotated cubic bezier, reusing `arcBezier`). All loops of one hatch
 *        are carried as the SUBPATHS of a SINGLE fill-only `PathPrimitive` with
 *        the EVEN-ODD rule, so multiple boundary paths model holes correctly
 *        (outer − inner). `stroke: false` → the backend emits `f*` only (AutoCAD
 *        solid hatches fill without stroking their boundary).
 *
 *   P2 — PATTERN fill (`solidFill === false`, non-gradient). The boundary loops
 *        become a PDF CLIP region; the resolved pattern line families
 *        (`hatchPatternLines`, reusing the scene's AutoCAD `.pat` definitions
 *        via `scene3d/hatch/hatchPatterns.getPatternDefinition`) are stroked
 *        across the boundary bbox and clipped to the region — no manual
 *        line/boundary intersection. Emitted as a `HatchPatternPrimitive` so the
 *        backend wraps it in its own `q · W* n · strokes · Q` block.
 *
 *   P3 — GRADIENT (PDF-6.5b — REAL axial/radial shading). AutoCAD gradient
 *        hatches carry their colour ramp in DXF group codes 450–470, now
 *        captured on the typed `HatchEntity.gradient` model (PDF-6.5a). When a
 *        hatch carries `gradient.kind === 1` we emit a {@link
 *        GradientHatchPrimitive}: the boundary loops clip a PDF axial
 *        (ShadingType 2) or radial (ShadingType 3) shading whose colour ramp is
 *        resolved from the gradient stops. The legacy SOLID fallback survives
 *        ONLY for a defensive case (a gradient pattern NAME with no `gradient`
 *        block — e.g. a hand-authored DXF lacking the 6.5a codes) so such a
 *        hatch is never invisible.
 *
 * ── $FILLMODE 0: ONE outline tier replaces all three ───────────────────────
 * A fourth state sits AHEAD of P1/P2/P3 and is not a tier of its own so much as
 * their common OFF switch. Autodesk, "To turn on or off the display of solid
 * fills": "When you turn off Fill mode, wide polylines, solid-filled polygons,
 * gradient fill, and hatches are displayed in outline form"; FILL (Command),
 * Off: "Only the outlines of objects are displayed and plotted" — "and
 * plotted" is why this converter, not only the canvas, has to read it. The
 * list is unqualified: `gradient fill` is named outright and `hatches` carries
 * no "solid" qualifier, so P1, P2 AND P3 are suppressed together and the SAME
 * boundary loops are STROKED instead ({@link hatchLoopsPrimitive}).
 *
 * `ezdxf` draws that state verbatim under `HatchPolicy.SHOW_OUTLINE` ("show
 * only the outline of HATCH entities"): `addons/drawing/frontend.py::
 * draw_hatch_entity` replaces `filling` with a plain `Filling()` BEFORE the
 * `filling.type == Filling.PATTERN` test — so a patterned hatch never reaches
 * `draw_hatch_pattern` — and then routes the boundary paths to `draw_path`
 * (stroked) instead of `draw_filled_paths`.
 *
 * The value arrives on `CadConvertContext.fillmode` (`DrawingDocument.fillmode`,
 * threaded by `sheetRenderer.ts`) and is read through
 * `scene3d/adapters/geom/polylineDrawnBand.ts::fillModePaintsSolid` — the SAME
 * channel and the SAME predicate `converters/widePolylineBand.ts` has used
 * since @codex PR #785 round 12, and the same one the canvas applies to
 * `RenderableEntity.fillmode`. One document variable, one reader contract, so
 * screen and paper cannot hold two opinions about it (@codex PR #785 round 16,
 * P2 — the round-12 fix landed on the wide polyline and left the hatch behind,
 * which is how one drawing came to outline its bands and fill its hatches).
 *
 * RAW world mm coordinates throughout (the page / VP transform applies
 * downstream, like every other converter).
 *
 * ── MODEL (gradient) ───────────────────────────────────────────────────────
 * `HatchEntity.gradient` (model.ts `hatchGradientSchema`) carries the full
 * AutoCAD gradient ramp: `kind` (450, 1=gradient), `oneColor` (452,
 * 0=two-colour / 1=single+tint), `numberOfColors` (453), `rotation` (460,
 * RADIANS), `shift` (461, 0..1), `tint` (462, 0..1), `name` (470) and the
 * `colors[]` stops (each with `position`, optional `aci`/`trueColor`). We map
 * the gradient NAME to a {@link GradientHatchSpec} (axial/radial + inverse +
 * symmetric), resolve the stop colours to plot RGB, and carry the boundary
 * loops + bbox for the emit-time axis/disc geometry.
 */

import type {
  HatchEntity,
  HatchEdgeCurve,
  HatchLoopEmitter,
  BezierLoop,
  EdgeSegment,
} from "@dxf-ai-editor/drawing-model";
import { walkHatchBoundary, bezierLoopsBbox } from "@dxf-ai-editor/drawing-model";

import type {
  CadConvertContext,
  CadPrimitive,
  GradientHatchPrimitive,
  GradientHatchSpec,
  HatchPatternPrimitive,
  PathPrimitive,
  PathSubpath,
  Point2,
} from "../cadPrimitive";
import type { RgbColor } from "../pdfTransform";
import { aciToPlotRgb, hexToPlotRgb } from "../pdfTransform";
import { resolvePlotStyle } from "./resolveStyle";
// The ONE `$FILLMODE` predicate (absent ⇒ the documented initial value 1) —
// shared with the canvas and with the wide-polyline band plot converter.
import { fillModePaintsSolid } from "../../../scene3d/adapters/geom/polylineDrawnBand";
import {
  hatchPatternLines,
  isGradientPatternName,
} from "./hatchPattern";
// Shared gradient-ramp resolution (scene3d/hatch — imported UP, like
// `getPatternDefinition`; see gradientRamp.ts dep-graph note). The PDF caller
// supplies its PLOT-tweaked colour resolvers (white → black on the page);
// `resolveGradientSpec` below is now a thin wrapper around the shared core.
import {
  resolveGradientRamp,
  type StopColorResolvers,
} from "../../../scene3d/hatch/gradientRamp";

/* ── Boundary walk (shared) → PDF bezier loops ─────────────────────────── */

/**
 * The PDF projection of the shared {@link walkHatchBoundary} traversal. It
 * reproduces the former `buildLoop` logic VERBATIM (byte-identical PDF output —
 * existing tests are the regression fence):
 *
 *   - `beginLoop` resets the running `start`/`segments` accumulator.
 *   - `emitEdge(curve, isFirstEdge)` appends one edge's exact cubics. The first
 *     emitted edge seeds `start` with the curve's start point. A NON-first
 *     ARC / ellipticArc edge first pushes a straight `{kind:'line'}` JOIN to the
 *     curve's start (boundary edges are contiguous in AutoCAD — a no-op when
 *     they already coincide, a correct join otherwise). A LINE edge inserts NO
 *     join: the running pen already sits at the previous edge's end and the
 *     line's own segment draws straight to `edge.end` (matching the former
 *     line-branch, which never emitted a join — preserving the segment stream).
 *   - `endLoop` finalises `{start, segments}`, or `null` when no usable edge
 *     contributed (`start === null || segments.length === 0`).
 *
 * Each loop's `start` is the FIRST edge's start point; consecutive edges chain
 * their geometry. We do NOT force-close with an explicit segment — the subpath
 * is flagged closed and the backend emits `closePath` (`h`), drawing the
 * implicit closing line back to `start` (AutoCAD boundary loops are always
 * closed). DXF degrees → radians is applied ONCE inside `edgeToCurve`.
 */
function makePdfBezierEmitter(): HatchLoopEmitter<BezierLoop> {
  let start: Point2 | null = null;
  let segments: EdgeSegment[] = [];
  return {
    beginLoop(): void {
      start = null;
      segments = [];
    },
    emitEdge(curve: HatchEdgeCurve, isFirstEdge: boolean): void {
      const { start: cStart, segments: cSegs } = curve.toBeziers();
      if (isFirstEdge) {
        start = cStart;
      } else if (curve.kind !== "line") {
        // Connect the running path to a non-contiguous arc/ellipse start with a
        // straight join (the former arc/ellipticArc branch did exactly this; the
        // line branch never joined — the line segment already draws to its end).
        segments.push({ kind: "line", to: cStart });
      }
      for (const s of cSegs) segments.push(s);
    },
    endLoop(): BezierLoop | null {
      if (start === null || segments.length === 0) return null;
      return { start, segments };
    },
  };
}

const pdfBezierEmitter = makePdfBezierEmitter();

/**
 * Convert a HATCH to its CadPrimitive list.
 *
 *   - 0 boundary paths (legal / degenerate, §7.2-style guard) → `[]`.
 *   - `$FILLMODE` 0 → one STROKE-ONLY `PathPrimitive` over the same loops, for
 *     a solid, a gradient and a patterned hatch alike (see the module header).
 *   - Gradient (`gradient.kind === 1`, PDF-6.5b) → one
 *     {@link GradientHatchPrimitive} (boundary clip + axial/radial PDF shading).
 *   - SOLID → one fill-only even-odd `PathPrimitive` carrying every loop as a
 *     subpath.
 *   - Gradient pattern NAME with no `gradient` block (legacy / hand-authored
 *     DXF) → documented solid fallback (one fill-only even-odd `PathPrimitive`,
 *     plot colour) so it is never invisible.
 *   - Pattern → one `HatchPatternPrimitive` (clip + pattern strokes).
 */
export function hatchToPrimitives(
  entity: HatchEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  // §7.2-style guard: a hatch with no boundary paths is legal but invisible.
  if (entity.boundaryPaths.length === 0) return [];

  // Shared canonical boundary walk (drawing-model) → exact cubic-bezier loops.
  // The pdfBezierEmitter reproduces the prior buildLoop logic verbatim (seed
  // start, straight join for a non-contiguous edge start, concat segments).
  const loops = walkHatchBoundary(entity.boundaryPaths, pdfBezierEmitter);
  // All loops degenerated (e.g. zero-radius edges) → nothing to fill.
  if (loops.length === 0) return [];
  // Byte-identity fence: a BezierLoop is field-identical to a PathSubpath, so
  // the loops feed hatchLoopsPrimitive / pattern / gradient with no copy. This
  // compile-time guard makes any field drift between the two shapes fail tsc
  // (not surface as a runtime mismatch).
  const _byteIdentityGuard = (l: BezierLoop): PathSubpath => l;
  void _byteIdentityGuard;

  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  const layer = entity.layer ?? "0";

  // `$FILLMODE` 0 — the drawing says nothing is filled, so NONE of the three
  // tiers below runs and the boundary is stroked instead. Placed ahead of all
  // of them deliberately: a gradient hatch and a patterned one are as unfilled
  // as a solid one in that state (see the module header's FILLMODE section),
  // and a gate inside the solid branch alone would be the same bug one tier
  // over. Absent ⇒ 1, so a default drawing emits exactly what it did before.
  if (!fillModePaintsSolid(ctx.fillmode)) {
    return [hatchLoopsPrimitive(entity.id, layer, loops, style, false)];
  }

  // PDF-6.5b — a hatch carrying a structured gradient ramp (DXF 450=1) renders
  // as a REAL axial/radial PDF shading clipped to the boundary. This MUST NOT
  // fall through to the solid path (CLAUDE.md PRIORITY #1: no simplification for
  // an entity that HAS a gradient).
  if (entity.gradient !== undefined && entity.gradient.kind === 1) {
    const bbox = bezierLoopsBbox(loops);
    if (!bbox) return [];
    const gradient = resolveGradientSpec(entity, style.color);
    const prim: GradientHatchPrimitive = {
      type: "gradient-hatch",
      entityId: entity.id,
      layer,
      loops,
      bbox,
      gradient,
      style,
    };
    return [prim];
  }

  // SOLID fill, OR gradient documented-fallback (gradient NAME but no model
  // ramp — defensive; a real AutoCAD gradient always carries the 6.5a block).
  if (entity.solidFill || isGradientPatternName(entity.patternName)) {
    return [hatchLoopsPrimitive(entity.id, layer, loops, style, true)];
  }

  // Pattern fill (P2): clip to the boundary + stroke the pattern line families.
  const bbox = bezierLoopsBbox(loops);
  if (!bbox) return [];
  const lines = hatchPatternLines(
    entity.patternName,
    entity.patternScale,
    entity.patternAngle,
    entity.doubleLine,
    bbox,
  );
  // No usable line family (and not solid) → emit nothing rather than fake a
  // solid fill silently (CLAUDE.md PRIORITY #1). The unknown-pattern generic
  // fallback inside hatchPatternLines guarantees ≥1 family for any name, so
  // this only triggers for a degenerate bbox.
  const prim: HatchPatternPrimitive = {
    type: "hatch-pattern",
    entityId: entity.id,
    layer,
    loops,
    lines,
    color: style.color,
    style,
  };
  return [prim];
}

/**
 * The hatch's loops as ONE closed multi-subpath {@link PathPrimitive}, painted
 * the way the drawing's `$FILLMODE` calls for. The first loop seeds
 * `start`/`segments`; the rest go in `subpaths`, so a single paint op covers
 * every loop of the hatch.
 *
 *   `solid: true`  — FILL-ONLY, even-odd (`f*`): AutoCAD fills a solid hatch
 *     without stroking its boundary (the boundary polyline, where one is drawn,
 *     is a separate entity), and even-odd is what turns an inner loop into the
 *     hole it is.
 *   `solid: false` — STROKE-ONLY (`fill: "none"`, `stroke` left at its default
 *     `true`): the `$FILLMODE` 0 outline. EVERY loop is stroked, interior ones
 *     included — in outline form a hole's edge is ink like any other boundary,
 *     which is also what `ezdxf`'s `HatchPolicy.SHOW_OUTLINE` emits (it walks
 *     the same rendered-path set and calls `draw_path` on each).
 *
 * `closed: true` makes the backend emit `h` per subpath before the single paint
 * op, and `batchPaint.ts` walks `subpaths` for a stroke exactly as it does for
 * a fill — the same two properties `widePolylineBand.ts::bandPathPrimitive`
 * relies on for the band's annulus in ITS zero state, and the reason both
 * states are one function here as they are there: the GEOMETRY is
 * byte-identical either way, because `$FILLMODE` decides the paint op and never
 * the shape.
 */
function hatchLoopsPrimitive(
  entityId: string,
  layer: string,
  loops: PathSubpath[],
  style: PathPrimitive["style"],
  solid: boolean,
): PathPrimitive {
  const first = loops[0]!;
  const rest = loops.slice(1);
  return {
    type: "path",
    entityId,
    layer,
    start: first.start,
    segments: first.segments,
    closed: true,
    fill: solid ? "evenodd" : "none",
    ...(solid ? { stroke: false } : {}),
    subpaths: rest.length > 0 ? rest : undefined,
    style,
  };
}

/* ── Gradient resolution (PDF-6.5b) ───────────────────────────────────── */

/**
 * PLOT-tweaked colour resolvers handed to the shared {@link resolveGradientRamp}
 * core. Both apply the page tweak (white → black so a true-white stop reads on
 * the white page exactly like any other plotted colour) — that policy is
 * PDF-specific and lives here, NOT in the shared module (the scene caller passes
 * faithful, untweaked resolvers instead).
 */
const PDF_STOP_RESOLVERS: StopColorResolvers = {
  hexToRgb: (hex) => hexToPlotRgb(hex),
  aciToRgb: (aci) => aciToPlotRgb(aci),
};

/**
 * Resolve a hatch's gradient ramp + flags into a {@link GradientHatchSpec}.
 *
 * Thin wrapper over the SHARED {@link resolveGradientRamp} core (scene3d/hatch/
 * gradientRamp — one source of truth across the PDF + scene render pipes). It
 * passes the PDF plot-tweaked colour resolvers and projects the returned
 * {@link import("../../../scene3d/hatch/gradientRamp").GradientRampSpec} onto the
 * PDF `GradientHatchSpec` shape (field-identical apart from the `curved` flag,
 * which the PDF carries on its own `ShadingSpec`).
 *
 * See {@link resolveGradientRamp} for the oneColor tint-toward-white + INV*
 * swap semantics (documented AutoCAD-APPEARANCE approximations).
 */
function resolveGradientSpec(
  entity: HatchEntity,
  fallback: RgbColor,
): GradientHatchSpec {
  const ramp = resolveGradientRamp(entity.gradient!, fallback, PDF_STOP_RESOLVERS);
  return {
    name: ramp.name,
    shadingType: ramp.shadingType,
    inverse: ramp.inverse,
    symmetric: ramp.symmetric,
    rotation: ramp.rotation,
    shift: ramp.shift,
    oneColor: ramp.oneColor,
    tint: ramp.tint,
    color0: ramp.color0,
    color1: ramp.color1,
  };
}

